import React, { useCallback } from 'react'
import { toast, ToastOptions } from 'react-toastify'
import { v4 as uuidv4 } from 'uuid'
import { useAppSelector } from 'app/hooks'
import { ToastId } from 'features/ui/types'

export interface AddToastConfig extends ToastOptions {
  message: React.ReactNode
}

const getBaseToastId = (toastId: ToastId) => toastId.toString().split('@')[0]
const generateDuplicatedToastId = (toastId?: ToastId) => `${toastId}@${uuidv4()}`

const useToast = () => {
  const toasts = useAppSelector(({ ui }) => ui.toasts)

  const addToast = useCallback(
    (toastConfig: AddToastConfig) => {
      const { message, ...toastOptions } = toastConfig

      const duplicatedIds = toasts.filter((tid) => getBaseToastId(tid) === toastOptions.toastId)
      const hasDuplicatedToastId = duplicatedIds.length > 0
      let outputToastId

      if (hasDuplicatedToastId) {
        duplicatedIds.forEach((tid) => {
          toast.dismiss(tid)
        })

        const transformedToastId = generateDuplicatedToastId(toastOptions.toastId)
        toast(message, {
          ...toastOptions,
          toastId: transformedToastId
        })
        outputToastId = toastOptions.toastId
      } else {
        outputToastId = toast(message, toastOptions)
      }
      return outputToastId as ToastId
    },
    [toasts]
  )

  const removeToast = useCallback(
    (toastId: ToastId) => {
      const targetIds = toasts.filter((tid) => getBaseToastId(tid) === toastId)
      targetIds.forEach((tid) => {
        toast.dismiss(tid)
      })
    },
    [toasts]
  )

  return [addToast, removeToast] as [typeof addToast, typeof removeToast]
}

export default useToast
